import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import html2canvas from 'html2canvas';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronDown, Check, Loader2, Printer, Share2, FileDown, Image as ImageIcon, Sparkles, ClipboardList, ListOrdered, Link2, AlertTriangle, Target, ShieldAlert, LayoutDashboard, Trash2, Plus, ArrowUp, ArrowDown, X } from 'lucide-react';
import { toast } from '../../../lib/toast';
import PitchEditor from '../../../components/coach/PitchEditor';
import { useTasyarBrand } from '../../../lib/tasyarBrand';
import { API, auth, Field, exportPdf, PrintStyle, errMsg, inputCls, LEVEL_LABELS } from './shared';
import { Modal } from '../../organization/finance/shared';

const remapDiagramElements = (elements, swap) => {
  if (!swap) return elements;
  return elements.map((el) => {
    if (Array.isArray(el.points)) {
      const points = [];
      for (let i = 0; i < el.points.length; i += 2) points.push(el.points[i + 1], el.points[i]);
      return { ...el, points };
    }
    const next = { ...el, x: el.y, y: el.x };
    if (next.width != null && next.height != null) { next.width = el.height; next.height = el.width; }
    return next;
  });
};

const SectionCard = ({ icon: Icon, title, hint, children, testId, className = '' }) => (
  <section className={`rounded-2xl border border-slate-200/70 bg-white p-4 shadow-[0_1px_3px_rgba(0,0,0,0.04)] sm:p-5 ${className}`} data-testid={testId}>
    <header className="mb-4 flex items-start gap-3 border-b border-slate-100 pb-3.5">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-teal-50 to-emerald-50 text-teal-700 ring-1 ring-teal-100">
        <Icon className="h-4 w-4" strokeWidth={2.2} />
      </span>
      <div className="min-w-0">
        <h3 className="text-sm font-bold text-slate-800">{title}</h3>
        {hint && <p className="mt-0.5 text-[11px] leading-relaxed text-slate-500">{hint}</p>}
      </div>
    </header>
    <div className="space-y-4">{children}</div>
  </section>
);

const remapPositions = (pos, swap) => {
  if (!pos) return undefined;
  if (!swap) return pos;
  const out = {};
  Object.entries(pos).forEach(([k, v]) => { out[k] = { x: v.y, y: v.x }; });
  return out;
};

const CollapsibleSection = ({ title, icon: Icon = ClipboardList, defaultOpen = false, testId, children, className = '' }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className={`overflow-hidden rounded-2xl border border-slate-200/70 bg-white shadow-[0_1px_3px_rgba(0,0,0,0.04)] ${className}`} data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-3 px-4 py-3 text-right transition-colors hover:bg-slate-50/70"
        data-testid={`${testId}-toggle`}
      >
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-50 text-slate-500 ring-1 ring-slate-100">
          <Icon className="h-3.5 w-3.5" strokeWidth={2.2} />
        </span>
        <span className="flex-1 text-sm font-bold text-slate-800">{title}</span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      <div className={`${open ? 'block' : 'hidden'} space-y-3 border-t border-slate-100 px-4 py-4`}>
        {children}
      </div>
    </section>
  );
};

const IconBtn = ({ onClick, icon: Icon, label, testId, busy }) => (
  <button
    type="button" onClick={onClick} disabled={busy} title={label} data-testid={testId}
    className="flex h-10 items-center justify-center gap-1.5 rounded-xl border border-white/15 bg-white/10 px-3 text-xs font-bold text-white/85 backdrop-blur transition-all hover:border-white/30 hover:bg-white/20 active:scale-95 disabled:opacity-50"
  >
    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon className="h-4 w-4" />}
    <span className="hidden sm:inline">{label}</span>
  </button>
);

const SaveActions = ({ saving, saveIndicator, onDraft, onReady, dark }) => (
  <>
    <button
      type="button" onClick={onDraft} disabled={saving} data-testid="exercise-save-draft-btn"
      className={`flex h-11 flex-1 items-center justify-center gap-2 rounded-xl px-4 text-sm font-bold transition-all active:scale-95 disabled:opacity-50 lg:h-10 lg:flex-none ${dark ? 'border border-white/20 bg-white/10 text-white hover:bg-white/20' : 'border border-slate-200 bg-white text-slate-700 hover:border-teal-300'}`}
    >
      {saving && saveIndicator === 'saving' && <Loader2 className="h-4 w-4 animate-spin" />}
      حفظ كمسودة
    </button>
    <button
      type="button" onClick={onReady} disabled={saving} data-testid="exercise-mark-ready-btn"
      className="flex h-11 flex-1 items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-600 px-5 text-sm font-bold text-white shadow-lg shadow-emerald-500/25 transition-all hover:brightness-105 active:scale-95 disabled:opacity-50 lg:h-10 lg:flex-none"
    >
      {saving && saveIndicator === 'saving' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
      اعتبار التمرين جاهزاً
    </button>
  </>
);

const MetaBox = ({ label, value }) => (
  <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: '6px 10px' }}>
    <div style={{ color: '#64748b', fontSize: 10 }}>{label}</div>
    <div style={{ color: '#0f172a', fontWeight: 700 }}>{value}</div>
  </div>
);

const PrintSection = ({ title, children, style }) => (
  <div style={{ marginBottom: 14, ...style }}>
    <div style={{ fontSize: 13, fontWeight: 800, color: '#0f172a', borderRight: '3px solid #0d9488', paddingRight: 8, marginBottom: 6 }}>{title}</div>
    <div style={{ fontSize: 12, color: '#1e293b', lineHeight: 1.7 }}>{children}</div>
  </div>
);

const BulletList = ({ items }) => (
  <ul style={{ margin: 0, paddingRight: 18 }}>
    {items.map((it, i) => <li key={i} style={{ marginBottom: 3 }}>{it}</li>)}
  </ul>
);

const emptyForm = () => ({
  title: '', short_description: '', technical_objective: '', exercise_type: 'technical', level: 'intermediate',
  age_category: '', min_players: '', max_players: '', duration_minutes: '', field_space: '',
  equipment: [], steps: [], coaching_points: [], common_mistakes: [], success_indicators: [], safety_requirements: [],
  tags: [], status: 'draft', organization_id: null, team_id: null, category_id: null,
});

const ListEditor = ({ label, items, setItems, placeholder, testId }) => {
  const [val, setVal] = useState('');
  const add = () => { if (val.trim()) { setItems([...items, val.trim()]); setVal(''); } };
  return (
    <Field label={label}>
      <div className="flex gap-2">
        <input value={val} onChange={(e) => setVal(e.target.value)} placeholder={placeholder} className={inputCls} data-testid={`${testId}-input`}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} />
        <button type="button" onClick={add} data-testid={`${testId}-add-btn`}
          className="flex h-11 shrink-0 items-center gap-1 rounded-xl border border-slate-200 bg-slate-50 px-3.5 text-xs font-bold text-slate-600 transition-all hover:border-teal-300 hover:text-teal-700 active:scale-95">
          <Plus className="h-3.5 w-3.5" /> إضافة
        </button>
      </div>
      {items.length > 0 && (
        <div className="mt-2.5 flex flex-wrap gap-1.5">
          {items.map((it, i) => (
            <span key={i} className="group flex items-center gap-1 rounded-full border border-teal-100 bg-teal-50/80 py-1 pe-2.5 ps-1.5 text-xs font-semibold text-teal-800" data-testid={`${testId}-item-${i}`}>
              <button type="button" onClick={() => setItems(items.filter((_, idx) => idx !== i))} data-testid={`${testId}-remove-${i}`}
                className="flex h-4 w-4 items-center justify-center rounded-full bg-teal-200/60 text-teal-800 transition-colors hover:bg-red-500 hover:text-white">
                <X className="h-2.5 w-2.5" strokeWidth={3} />
              </button>
              {it}
            </span>
          ))}
        </div>
      )}
    </Field>
  );
};

const AiSuggestModal = ({ onClose, onApply }) => {
  const [objective, setObjective] = useState('');
  const [level, setLevel] = useState('intermediate');
  const [ageCategory, setAgeCategory] = useState('');
  const [durationMinutes, setDurationMinutes] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    if (!objective.trim()) { setError('يرجى تحديد هدف التمرين'); return; }
    setError(''); setLoading(true);
    try {
      const res = await axios.post(`${API}/coach-studio/exercises/ai-suggest`, {
        objective: objective.trim(), level, age_category: ageCategory || undefined,
        duration_minutes: durationMinutes ? Number(durationMinutes) : undefined, notes: notes || undefined,
      }, auth());
      onApply(res.data);
    } catch (e) {
      setError(errMsg(e, 'تعذر توليد الاقتراح، حاول مجدداً'));
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="ai-suggest-modal">
      <div className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-xl">
        <div className="flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-base font-bold text-slate-900">
            <Sparkles className="h-4 w-4 text-teal-600" /> اقترح لي تمريناً
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="ai-suggest-close-btn">×</button>
        </div>
        <p className="mt-1 text-xs text-slate-500">صف هدف التمرين ودع المساعد الذكي يقترح مسودة كاملة تقدر تعدلها قبل الحفظ.</p>

        {error && <div className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700" data-testid="ai-suggest-error">{error}</div>}

        <div className="mt-4 space-y-3">
          <Field label="هدف التمرين" required>
            <textarea value={objective} onChange={(e) => setObjective(e.target.value)} rows={2} placeholder="مثال: تحسين التمرير السريع تحت الضغط"
              className={inputCls} data-testid="ai-suggest-objective-input" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="المستوى">
              <select value={level} onChange={(e) => setLevel(e.target.value)} className={inputCls} data-testid="ai-suggest-level-select">
                {Object.entries(LEVEL_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Field>
            <Field label="الفئة العمرية">
              <input value={ageCategory} onChange={(e) => setAgeCategory(e.target.value)} placeholder="مثال: تحت 15 سنة" className={inputCls} data-testid="ai-suggest-age-input" />
            </Field>
          </div>
          <Field label="المدة بالدقائق (اختياري)">
            <input type="number" value={durationMinutes} onChange={(e) => setDurationMinutes(e.target.value)} className={inputCls} data-testid="ai-suggest-duration-input" />
          </Field>
          <Field label="ملاحظات إضافية (اختياري)">
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className={inputCls} data-testid="ai-suggest-notes-input" />
          </Field>
        </div>

        <div className="mt-5 flex gap-2">
          <button onClick={onClose} className="flex-1 rounded-lg border border-slate-200 py-2.5 text-sm font-semibold text-slate-600" data-testid="ai-suggest-cancel-btn">إلغاء</button>
          <button onClick={submit} disabled={loading} className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-gradient-to-l from-teal-600 to-teal-500 py-2.5 text-sm font-semibold text-white disabled:opacity-60" data-testid="ai-suggest-generate-btn">
            {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> جارٍ التوليد...</> : <><Sparkles className="h-4 w-4" /> توليد الاقتراح</>}
          </button>
        </div>
      </div>
    </div>
  );
};

export default function ExerciseEditorPage() {
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const brand = useTasyarBrand();
  const [form, setForm] = useState(emptyForm());
  const [exerciseId, setExerciseId] = useState(isNew ? null : id);
  const [diagrams, setDiagrams] = useState([]);
  const [activeDiagram, setActiveDiagram] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!isNew);
  const [memberships, setMemberships] = useState([]);
  const [selectedMembership, setSelectedMembership] = useState(null);
  const [scope, setScope] = useState({ teams: [], categories: [], players: [] });
  const [saveIndicator, setSaveIndicator] = useState('idle');
  const [exerciseTypes, setExerciseTypes] = useState([]);
  const [exportBusy, setExportBusy] = useState(false);
  const [pitchImages, setPitchImages] = useState([]);
  const [showAiModal, setShowAiModal] = useState(false);
  const [diagramVersion, setDiagramVersion] = useState(0);
  const [pendingAiDiagram, setPendingAiDiagram] = useState(null);
  const [isOwner, setIsOwner] = useState(true);
  const dirtyRef = useRef(false);
  const pitchEditorRef = useRef(null);
  const printRef = useRef(null);
  const exerciseIdRef = useRef(isNew ? null : id);
  const setExerciseIdSynced = (val) => { exerciseIdRef.current = val; setExerciseId(val); };

  useEffect(() => {
    axios.get(`${API}/coaches/me/memberships`, auth())
      .then((res) => setMemberships((res.data || []).filter((m) => m.status === 'active')))
      .catch(() => setMemberships([]));
    axios.get(`${API}/coach-studio/config`)
      .then((res) => setExerciseTypes(res.data?.exercise_types || []))
      .catch(() => setExerciseTypes([]));
  }, []);

  useEffect(() => {
    if (!selectedMembership) { setScope({ teams: [], categories: [], players: [] }); return; }
    axios.get(`${API}/coaches/me/memberships/${selectedMembership.id}/scope`, auth())
      .then((res) => setScope(res.data))
      .catch(() => setScope({ teams: [], categories: [], players: [] }));
  }, [selectedMembership]);

  useEffect(() => {
    if (isNew) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/coach-studio/exercises/${id}`, auth());
        const d = res.data;
        const NULLABLE_TEXT_FIELDS = ['short_description', 'technical_objective', 'age_category', 'field_space', 'min_players', 'max_players', 'duration_minutes'];
        const sanitized = { ...d };
        NULLABLE_TEXT_FIELDS.forEach((f) => { if (sanitized[f] === null || sanitized[f] === undefined) sanitized[f] = ''; });
        setForm({ ...emptyForm(), ...sanitized });
        setIsOwner(d.is_owner !== false);
        setDiagrams(d.diagrams || []);
        if ((d.diagrams || []).length > 0) { setActiveDiagram(d.diagrams[0]); setDiagramVersion((v) => v + 1); }
        setExerciseIdSynced(d.id);
      } catch (e) {
        toast.error(errMsg(e, 'تعذر تحميل التمرين'));
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  useEffect(() => {
    if (!form.organization_id || selectedMembership) return;
    const found = memberships.find((m) => m.club_id === form.organization_id);
    if (found) setSelectedMembership(found);
  }, [form.organization_id, memberships]);

  const set = (patch) => { dirtyRef.current = true; setForm((f) => ({ ...f, ...patch })); };

  const buildPayload = (f = form) => ({
    title: f.title, short_description: f.short_description || null, technical_objective: f.technical_objective || null,
    exercise_type: f.exercise_type, level: f.level, age_category: f.age_category || null,
    min_players: f.min_players ? Number(f.min_players) : null, max_players: f.max_players ? Number(f.max_players) : null,
    duration_minutes: f.duration_minutes ? Number(f.duration_minutes) : null, field_space: f.field_space || null,
    equipment: f.equipment, steps: f.steps.map((s, i) => ({ ...s, order: i })), coaching_points: f.coaching_points,
    common_mistakes: f.common_mistakes, success_indicators: f.success_indicators, safety_requirements: f.safety_requirements,
    tags: f.tags,
    organization_id: f.organization_id || null, team_id: f.team_id || null, category_id: f.category_id || null,
  });

  const applyAiDiagram = async (rawDiagram, sid, targetDiagram, mode) => {
    const liveScene = pitchEditorRef.current?.getScene?.();
    const baseScene = (liveScene && liveScene.elements) ? liveScene : (targetDiagram?.scene_json?.elements ? targetDiagram.scene_json : null);
    const orientation = baseScene?.orientation || 'vertical';
    const swap = orientation === 'horizontal';
    const newElements = remapDiagramElements(rawDiagram.elements, swap);
    const finalElements = mode === 'merge' && baseScene ? [...baseScene.elements, ...newElements] : newElements;
    const aiFrames = (rawDiagram.keyframes || []).map((f) => ({ ...f, positions: remapPositions(f.positions, swap) }));
    const aiStart = remapPositions(rawDiagram.anim_start, swap);
    const keyframes = mode === 'merge' && baseScene?.keyframes?.length ? [...baseScene.keyframes, ...aiFrames] : aiFrames;
    const animStart = mode === 'merge' && baseScene?.anim_start ? { ...baseScene.anim_start, ...(aiStart || {}) } : aiStart;
    const scene = {
      schema_version: 1,
      field_template: baseScene?.field_template || rawDiagram.field_template || 'full',
      orientation,
      elements: finalElements,
      team_a_color: baseScene?.team_a_color || '#2563eb',
      team_b_color: baseScene?.team_b_color || '#dc2626',
      show_names: baseScene?.show_names || false,
      field_background: baseScene?.field_background || 'green',
      keyframes,
      anim_start: animStart || null,
    };
    try {
      let doc;
      if (targetDiagram?.id) {
        const res = await axios.put(`${API}/coach-studio/diagrams/${targetDiagram.id}`, { title: targetDiagram.title, order: targetDiagram.order, scene_json: scene, exercise_id: sid }, auth());
        doc = res.data;
        setDiagrams((d) => d.map((x) => (x.id === doc.id ? doc : x)));
      } else {
        const res = await axios.post(`${API}/coach-studio/diagrams`, { title: 'رسم مقترح بالذكاء الاصطناعي', order: diagrams.length, scene_json: scene, exercise_id: sid }, auth());
        doc = res.data;
        setDiagrams((d) => [...d, doc]);
      }
      setActiveDiagram(doc);
      setDiagramVersion((v) => v + 1);
      toast.success(keyframes.length
        ? `تم توليد رسم تكتيكي متحرك (${keyframes.length} مشاهد) — اضغط تشغيل أسفل الملعب لمشاهدة الحركة`
        : (mode === 'merge' ? 'تمت إضافة اقتراح الذكاء الاصطناعي إلى الرسم الحالي' : 'تم توليد رسم تكتيكي مقترح — يمكنك سحب وتعديل أي عنصر بحرية'));
    } catch {
      toast.error('تعذر حفظ الرسم التكتيكي المقترح');
    }
  };

  const handleApplyAiSuggestion = async (data) => {
    const newForm = {
      ...form,
      title: data.title || '', short_description: data.short_description || '', technical_objective: data.technical_objective || '',
      exercise_type: data.exercise_type || form.exercise_type, level: data.level || form.level, age_category: data.age_category || '',
      min_players: data.min_players ?? '', max_players: data.max_players ?? '', duration_minutes: data.duration_minutes ?? '',
      field_space: data.field_space || '', equipment: data.equipment || [],
      steps: (data.steps || []).map((s, i) => ({ order: i, title: s.title || '', description: s.description || '' })),
      coaching_points: data.coaching_points || [], common_mistakes: data.common_mistakes || [], success_indicators: data.success_indicators || [],
      safety_requirements: data.safety_requirements || [], tags: data.tags || [],
    };
    setForm(newForm);
    setShowAiModal(false);

    let sid = exerciseId;
    try {
      const payload = buildPayload(newForm);
      if (!sid) {
        const res = await axios.post(`${API}/coach-studio/exercises`, payload, auth());
        sid = res.data.id; setExerciseIdSynced(sid);
        navigate(`/coach/studio/exercises/${sid}`, { replace: true });
      } else {
        await axios.put(`${API}/coach-studio/exercises/${sid}`, payload, auth());
      }
      dirtyRef.current = false;
    } catch (e) {
      dirtyRef.current = true;
      toast.error(errMsg(e, 'تعذر حفظ التمرين المقترح تلقائياً — يمكنك الحفظ يدوياً'));
    }

    const diagramElements = data.diagram?.elements;
    if (sid && Array.isArray(diagramElements) && diagramElements.length > 0) {
      const rawDiagram = {
        elements: diagramElements,
        field_template: data.diagram.field_template,
        keyframes: data.diagram.keyframes || [],
        anim_start: data.diagram.anim_start || null,
      };
      const liveScene = pitchEditorRef.current?.getScene?.();
      const hasExistingContent = (liveScene?.elements?.length > 0) || (activeDiagram?.scene_json?.elements?.length > 0);
      if (hasExistingContent) {
        setPendingAiDiagram({ rawDiagram, sid, targetDiagram: activeDiagram });
      } else {
        await applyAiDiagram(rawDiagram, sid, activeDiagram, 'replace');
      }
    } else {
      toast.success('تم توليد الاقتراح، يمكنك تعديله ثم الحفظ');
    }
  };

  const save = async (statusOverride) => {
    if (!form.title.trim()) { toast.error('عنوان التمرين مطلوب'); return null; }
    setSaving(true);
    setSaveIndicator('saving');
    try {
      const payload = buildPayload();
      let savedId = exerciseId;
      if (!exerciseId) {
        if (statusOverride) payload.status = statusOverride;
        const res = await axios.post(`${API}/coach-studio/exercises`, payload, auth());
        savedId = res.data.id; setExerciseIdSynced(savedId);
        navigate(`/coach/studio/exercises/${savedId}`, { replace: true });
      } else {
        await axios.put(`${API}/coach-studio/exercises/${exerciseId}`, payload, auth());
        if (statusOverride) await axios.put(`${API}/coach-studio/exercises/${exerciseId}/status?status=${statusOverride}`, {}, auth());
      }
      dirtyRef.current = false;
      setSaveIndicator('saved');
      setTimeout(() => setSaveIndicator('idle'), 2000);
      return savedId;
    } catch (e) {
      toast.error(errMsg(e, 'تعذر حفظ التمرين'));
      setSaveIndicator('idle');
      return null;
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const t = setInterval(() => { if (dirtyRef.current && form.title.trim()) save(); }, 20000);
    return () => clearInterval(t);
  }, [form, exerciseId]);

  useEffect(() => {
    const onBeforeUnload = (e) => { if (dirtyRef.current) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  const handleSaveDraft = async () => { const sid = await save('draft'); if (sid) toast.success('تم الحفظ كمسودة'); };
  const handlePublishReady = async () => { const sid = await save('ready'); if (sid) toast.success('التمرين جاهز الآن'); };

  const flushPitchSave = async () => {
    const live = pitchEditorRef.current?.getScene?.();
    if (live?.elements?.length > 0) { try { await pitchEditorRef.current.save(); } catch { /* ignore */ } }
  };

  const openDiagram = async (diagram) => {
    if (!exerciseId) { const sid = await save(); if (!sid) return; }
    await flushPitchSave();
    setActiveDiagram(diagram);
    setDiagramVersion((v) => v + 1);
  };

  const handleDiagramChange = async (scene) => {
    const exId = exerciseIdRef.current;
    if (!exId || !isOwner) return;
    if (activeDiagram?.id) {
      const res = await axios.put(`${API}/coach-studio/diagrams/${activeDiagram.id}`, { title: activeDiagram.title, order: activeDiagram.order, scene_json: scene, exercise_id: exId }, auth());
      setActiveDiagram(res.data);
      setDiagrams((d) => d.map((x) => (x.id === res.data.id ? res.data : x)));
    } else {
      const res = await axios.post(`${API}/coach-studio/diagrams`, { title: `رسم ${diagrams.length + 1}`, order: diagrams.length, scene_json: scene, exercise_id: exId }, auth());
      setDiagrams((d) => [...d, res.data]);
      setActiveDiagram(res.data);
    }
  };

  const deleteDiagram = async (diagId) => {
    await axios.delete(`${API}/coach-studio/diagrams/${diagId}`, auth());
    setDiagrams((d) => d.filter((x) => x.id !== diagId));
    if (activeDiagram?.id === diagId) setActiveDiagram(null);
  };

  const capturePitchImages = async () => {
    const targets = diagrams.length > 0 ? diagrams : (activeDiagram ? [activeDiagram] : []);
    if (targets.length === 0) return [];
    await flushPitchSave();
    const originalId = activeDiagram?.id;
    const results = [];
    for (const d of targets) {
      setActiveDiagram(d);
      setDiagramVersion((v) => v + 1);
      await new Promise((r) => setTimeout(r, 500));
      try {
        const img = await pitchEditorRef.current?.getPreviewImage();
        if (img) results.push({ title: d.title || 'رسم تكتيكي', img });
      } catch { /* skip this diagram */ }
    }
    const original = targets.find((d) => d.id === originalId);
    if (original) {
      setActiveDiagram(original);
      setDiagramVersion((v) => v + 1);
      await new Promise((r) => setTimeout(r, 300));
    }
    return results;
  };

  const preparePrintSheet = async () => {
    setExportBusy(true);
    try {
      const imgs = await capturePitchImages();
      setPitchImages(imgs);
      await new Promise((r) => setTimeout(r, 60));
      return imgs;
    } finally {
      setExportBusy(false);
    }
  };

  const handlePrintExercise = async () => { await preparePrintSheet(); setTimeout(() => window.print(), 100); };
  const handleDownloadPdf = async () => {
    await preparePrintSheet();
    setExportBusy(true);
    try { await exportPdf(printRef.current, `تمرين-${form.title || 'تدريبي'}`); }
    catch { toast.error('تعذر إنشاء ملف PDF'); }
    finally { setExportBusy(false); }
  };
  const handleDownloadPng = async () => {
    await preparePrintSheet();
    setExportBusy(true);
    try {
      const canvas = await html2canvas(printRef.current, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
      const link = document.createElement('a');
      link.download = `تمرين-${form.title || 'تدريبي'}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch { toast.error('تعذر إنشاء صورة PNG'); }
    finally { setExportBusy(false); }
  };
  const handleShareExercise = async () => {
    await preparePrintSheet();
    setExportBusy(true);
    try {
      const canvas = await html2canvas(printRef.current, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
      canvas.toBlob(async (blob) => {
        if (!blob) return;
        const file = new File([blob], `${form.title || 'تمرين'}.png`, { type: 'image/png' });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          try { await navigator.share({ files: [file], title: form.title || 'تمرين تدريبي' }); return; } catch { return; }
        }
        const link = document.createElement('a');
        link.download = file.name;
        link.href = URL.createObjectURL(blob);
        link.click();
        toast.success('تم تحميل صورة التمرين — يمكنك مشاركتها من ملفاتك');
      }, 'image/png');
    } catch { toast.error('تعذر تحضير المشاركة'); }
    finally { setExportBusy(false); }
  };

  if (loading) return <div className="p-8 text-center text-sm text-slate-400" data-testid="exercise-editor-loading">جاري التحميل...</div>;

  const readinessChecks = [
    { label: 'العنوان', done: Boolean(form.title.trim()) },
    { label: 'الهدف الفني', done: Boolean(form.technical_objective) },
    { label: 'الوصف', done: Boolean(form.short_description) },
    { label: 'المدة', done: Boolean(form.duration_minutes) },
    { label: 'عدد اللاعبين', done: Boolean(form.min_players || form.max_players) },
    { label: 'الأدوات', done: form.equipment.length > 0 },
    { label: 'المراحل', done: form.steps.length > 0 },
    { label: 'نقاط التدريب', done: form.coaching_points.length > 0 },
    { label: 'رسم الملعب', done: diagrams.length > 0 },
  ];
  const readiness = Math.round((readinessChecks.filter((c) => c.done).length / readinessChecks.length) * 100);
  const exerciseTypeLabel = exerciseTypes.find((t) => t.value === form.exercise_type)?.label_ar || form.exercise_type;
  const teamName = scope.teams.find((t) => t.id === form.team_id)?.name;
  const categoryName = scope.categories.find((c) => c.id === form.category_id)?.name;
  const orgName = selectedMembership?.club_name;

  return (
    <div className="space-y-6 pb-28 lg:pb-6" data-testid="exercise-editor-page">
      <PrintStyle />
      <style>{`
        @media print {
          #root { display: none !important; }
          body { margin: 0 !important; }
          .exercise-print-sheet {
            position: static !important;
            width: 100% !important;
            max-width: 186mm !important;
            margin: 0 auto !important;
            padding-bottom: 4px !important;
          }
          .exercise-print-sheet img { break-inside: avoid; page-break-inside: avoid; }
          .exercise-print-sheet > div:last-child { margin-bottom: 0 !important; }
        }
      `}</style>

      <div className="relative overflow-hidden rounded-3xl bg-[#0e1726] p-4 shadow-[0_18px_45px_-20px_rgba(10,20,35,0.75)] sm:p-6" data-testid="exercise-editor-hero">
        <span className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full bg-emerald-500/20 blur-3xl" />
        <span className="pointer-events-none absolute -left-10 bottom-0 h-40 w-40 rounded-full bg-teal-400/10 blur-3xl" />
        <div className="relative flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2.5">
              <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-gradient-to-br from-teal-400 to-emerald-600 text-white shadow-lg shadow-emerald-500/30">
                <LayoutDashboard className="h-5 w-5" strokeWidth={2.2} />
              </span>
              <h2 className="text-lg font-extrabold text-white sm:text-xl" data-testid="exercise-editor-title">{isNew && !exerciseId ? 'إنشاء تمرين' : 'تعديل التمرين'}</h2>
              {!isOwner && <span className="rounded-full bg-sky-400/10 px-2.5 py-1 text-[11px] font-bold text-sky-300 ring-1 ring-sky-400/25" data-testid="exercise-library-notice">من مكتبة تسيار — انسخه لتعديله</span>}
              <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold ring-1 ${form.status === 'ready' ? 'bg-emerald-500/15 text-emerald-300 ring-emerald-400/30' : 'bg-amber-400/10 text-amber-300 ring-amber-400/25'}`} data-testid="exercise-status-chip">
                {form.status === 'ready' ? 'جاهز' : 'مسودة'}
              </span>
              {saveIndicator === 'saving' && <span className="flex items-center gap-1 text-[11px] font-bold text-emerald-300" data-testid="exercise-save-indicator"><Loader2 className="h-3.5 w-3.5 animate-spin" /> جارٍ الحفظ...</span>}
              {saveIndicator === 'saved' && <span className="flex items-center gap-1 text-[11px] font-bold text-emerald-300" data-testid="exercise-save-indicator"><Check className="h-3.5 w-3.5" /> تم الحفظ</span>}
            </div>
            <p className="mt-2 max-w-xl text-xs leading-relaxed text-white/55">
              اكتب تفاصيل التمرين، ارسمه على الملعب، وحرّكه كمشاهد — ثم صدّره لطاقمك أو لاعبيك بضغطة.
            </p>
            <button type="button" onClick={() => setShowAiModal(true)} data-testid="ai-suggest-open-btn"
              title={isNew && !exerciseId ? '' : 'يعيد الذكاء الاصطناعي توليد رسم تكتيكي جديد، ويمكنك اختيار استبداله أو إضافته للرسم الحالي'}
              className="group mt-3.5 inline-flex items-center gap-2 rounded-xl border border-emerald-400/30 bg-emerald-400/10 px-4 py-2.5 text-xs font-bold text-emerald-300 transition-all hover:bg-emerald-400/20 active:scale-95">
              <Sparkles className="h-4 w-4 transition-transform group-hover:rotate-12" />
              {isNew && !exerciseId ? 'اقترح لي تمريناً بالذكاء الاصطناعي' : 'اقترح لي رسماً جديداً'}
            </button>
          </div>

          <div className="flex flex-col items-stretch gap-3 lg:items-end">
            <div className="flex flex-wrap items-center gap-2">
              <IconBtn onClick={handlePrintExercise} icon={Printer} label="طباعة" testId="exercise-print-btn" busy={exportBusy} />
              <IconBtn onClick={handleShareExercise} icon={Share2} label="مشاركة" testId="exercise-share-btn" busy={exportBusy} />
              <IconBtn onClick={handleDownloadPdf} icon={FileDown} label="PDF" testId="exercise-pdf-btn" busy={exportBusy} />
              <IconBtn onClick={handleDownloadPng} icon={ImageIcon} label="PNG" testId="exercise-png-btn" busy={exportBusy} />
            </div>
            <div className="hidden items-center gap-2 lg:flex">
              <SaveActions saving={saving} saveIndicator={saveIndicator} onDraft={handleSaveDraft} onReady={handlePublishReady} dark />
            </div>
          </div>
        </div>

        <div className="relative mt-5 border-t border-white/10 pt-4">
          <div className="mb-2 flex items-center justify-between text-[11px] font-bold">
            <span className="text-white/60">جاهزية التمرين</span>
            <span className="text-emerald-300" data-testid="exercise-readiness-value">{readiness}%</span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-white/10">
            <div className="h-full rounded-full bg-gradient-to-l from-teal-400 to-emerald-400 transition-all duration-500" style={{ width: `${readiness}%` }} />
          </div>
          <div className="mt-2.5 flex flex-wrap gap-1.5">
            {readinessChecks.map((c) => (
              <span key={c.label} className={`rounded-full px-2 py-0.5 text-[10.5px] font-semibold ${c.done ? 'bg-emerald-400/15 text-emerald-300' : 'bg-white/5 text-white/40'}`}>
                {c.done ? '✓ ' : ''}{c.label}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:gap-6">
        <div className="contents lg:flex lg:min-w-0 lg:flex-1 lg:flex-col lg:gap-4">
          <SectionCard icon={ClipboardList} title="بيانات التمرين" hint="العنوان والهدف وتفاصيل التنفيذ الأساسية" testId="exercise-basic-info-section" className="order-1">
            <Field label="عنوان التمرين" required><input value={form.title} onChange={(e) => set({ title: e.target.value })} className={inputCls} placeholder="مثال: تمرير سريع في مساحات ضيقة" data-testid="exercise-title-input" /></Field>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="وصف مختصر"><textarea value={form.short_description} onChange={(e) => set({ short_description: e.target.value })} className={inputCls} rows={3} data-testid="exercise-description-input" /></Field>
              <Field label="الهدف الفني"><textarea value={form.technical_objective} onChange={(e) => set({ technical_objective: e.target.value })} className={inputCls} rows={3} data-testid="exercise-objective-input" /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Field label="نوع التمرين">
                <select value={form.exercise_type} onChange={(e) => set({ exercise_type: e.target.value })} className={inputCls} data-testid="exercise-type-select">
                  {exerciseTypes.length === 0 && <option value={form.exercise_type}>{form.exercise_type}</option>}
                  {exerciseTypes.map((t) => <option key={t.value} value={t.value}>{t.label_ar}</option>)}
                </select>
              </Field>
              <Field label="المستوى">
                <select value={form.level} onChange={(e) => set({ level: e.target.value })} className={inputCls} data-testid="exercise-level-select">
                  {Object.entries(LEVEL_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </Field>
              <Field label="الفئة العمرية"><input value={form.age_category} onChange={(e) => set({ age_category: e.target.value })} className={inputCls} placeholder="تحت 15" data-testid="exercise-age-input" /></Field>
              <Field label="المدة (دقيقة)"><input type="number" value={form.duration_minutes} onChange={(e) => set({ duration_minutes: e.target.value })} className={inputCls} data-testid="exercise-duration-input" /></Field>
              <Field label="أدنى عدد لاعبين"><input type="number" value={form.min_players} onChange={(e) => set({ min_players: e.target.value })} className={inputCls} data-testid="exercise-min-players-input" /></Field>
              <Field label="أقصى عدد لاعبين"><input type="number" value={form.max_players} onChange={(e) => set({ max_players: e.target.value })} className={inputCls} data-testid="exercise-max-players-input" /></Field>
              <div className="col-span-2">
                <Field label="مساحة الملعب"><input value={form.field_space} onChange={(e) => set({ field_space: e.target.value })} className={inputCls} placeholder="30×20 متر" data-testid="exercise-space-input" /></Field>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <ListEditor label="الأدوات المطلوبة" items={form.equipment} setItems={(v) => set({ equipment: v })} placeholder="أقماع، كرات..." testId="exercise-equipment" />
              <ListEditor label="الوسوم" items={form.tags} setItems={(v) => set({ tags: v })} placeholder="وسم..." testId="exercise-tags" />
            </div>
          </SectionCard>

          <SectionCard icon={ListOrdered} title="مراحل التنفيذ ونقاط التدريب" hint="رتّب المراحل بالأسهم، وأضف ملاحظات لكل مرحلة" testId="exercise-steps-section" className="order-3">
            <div className="space-y-2.5">
              {form.steps.map((s, i) => (
                <div key={i} className="group rounded-xl border border-slate-200 bg-slate-50/60 p-2.5 transition-colors hover:border-teal-200" data-testid={`exercise-step-${i}`}>
                  <div className="flex items-center gap-2">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#16233b] text-[11px] font-extrabold text-white">{i + 1}</span>
                    <input value={s.title} onChange={(e) => { const steps = [...form.steps]; steps[i] = { ...s, title: e.target.value }; set({ steps }); }} className={inputCls} placeholder="عنوان المرحلة" data-testid={`exercise-step-title-${i}`} />
                    <div className="flex shrink-0 gap-1">
                      <button type="button" disabled={i === 0} onClick={() => { const steps = [...form.steps]; [steps[i - 1], steps[i]] = [steps[i], steps[i - 1]]; set({ steps }); }} className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-colors hover:border-teal-300 disabled:opacity-30" data-testid={`exercise-step-up-${i}`}><ArrowUp className="h-3.5 w-3.5" /></button>
                      <button type="button" disabled={i === form.steps.length - 1} onClick={() => { const steps = [...form.steps]; [steps[i + 1], steps[i]] = [steps[i], steps[i + 1]]; set({ steps }); }} className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-colors hover:border-teal-300 disabled:opacity-30" data-testid={`exercise-step-down-${i}`}><ArrowDown className="h-3.5 w-3.5" /></button>
                      <button type="button" onClick={() => set({ steps: form.steps.filter((_, idx) => idx !== i) })} className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-red-500 transition-colors hover:border-red-300" data-testid={`exercise-step-remove-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </div>
                  <textarea
                    value={s.description || ''}
                    onChange={(e) => { const steps = [...form.steps]; steps[i] = { ...s, description: e.target.value }; set({ steps }); }}
                    rows={2} placeholder="شرح المرحلة (اختياري)"
                    className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700 outline-none transition-colors focus:border-teal-400"
                    data-testid={`exercise-step-desc-${i}`}
                  />
                </div>
              ))}
              <button type="button" onClick={() => set({ steps: [...form.steps, { order: form.steps.length, title: '', description: '' }] })} className="flex min-h-11 w-full items-center justify-center gap-1.5 rounded-xl border border-dashed border-slate-300 text-xs font-bold text-slate-500 transition-all hover:border-teal-300 hover:text-teal-700 active:scale-[0.99]" data-testid="exercise-add-step-btn">
                <Plus className="h-3.5 w-3.5" /> إضافة مرحلة
              </button>
            </div>
            <ListEditor label="نقاط التدريب والتعليمات" items={form.coaching_points} setItems={(v) => set({ coaching_points: v })} placeholder="نقطة تدريب..." testId="exercise-coaching-points" />
          </SectionCard>

          {memberships.length > 0 && (
            <CollapsibleSection title="ربط بالمنشأة (يتيح اختيار لاعبين حقيقيين في الملعب)" icon={Link2} testId="exercise-org-link-section" className="order-4">
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                <select
                  className={inputCls} data-testid="exercise-membership-select"
                  value={selectedMembership?.id || ''}
                  onChange={(e) => {
                    const m = memberships.find((x) => x.id === e.target.value) || null;
                    setSelectedMembership(m);
                    set({ organization_id: m?.club_id || null, team_id: null, category_id: null });
                  }}
                >
                  <option value="">بدون ربط</option>
                  {memberships.map((m) => <option key={m.id} value={m.id}>{m.club_name}</option>)}
                </select>
                <select className={inputCls} data-testid="exercise-team-select" value={form.team_id || ''} disabled={!selectedMembership} onChange={(e) => set({ team_id: e.target.value || null })}>
                  <option value="">بدون فريق</option>
                  {scope.teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
                <select className={inputCls} data-testid="exercise-category-select" value={form.category_id || ''} disabled={!selectedMembership} onChange={(e) => set({ category_id: e.target.value || null })}>
                  <option value="">بدون فئة</option>
                  {scope.categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            </CollapsibleSection>
          )}

          <CollapsibleSection title="الأخطاء الشائعة" icon={AlertTriangle} testId="exercise-mistakes-section" className="order-5">
            <ListEditor label="" items={form.common_mistakes} setItems={(v) => set({ common_mistakes: v })} placeholder="خطأ شائع..." testId="exercise-common-mistakes" />
          </CollapsibleSection>
          <CollapsibleSection title="مؤشرات النجاح" icon={Target} testId="exercise-success-section" className="order-6">
            <ListEditor label="" items={form.success_indicators} setItems={(v) => set({ success_indicators: v })} placeholder="مؤشر نجاح..." testId="exercise-success-indicators" />
          </CollapsibleSection>
          <CollapsibleSection title="اشتراطات السلامة" icon={ShieldAlert} testId="exercise-safety-section" className="order-7">
            <ListEditor label="" items={form.safety_requirements} setItems={(v) => set({ safety_requirements: v })} placeholder="اشتراط سلامة..." testId="exercise-safety" />
          </CollapsibleSection>
        </div>

        <div className="order-2 lg:sticky lg:top-20 lg:w-[53%] lg:shrink-0" data-testid="exercise-diagrams-section">
          <div className="overflow-hidden rounded-2xl border border-slate-200/70 bg-white shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 bg-gradient-to-l from-slate-50 to-white px-4 py-3">
              <h3 className="flex items-center gap-2 text-sm font-bold text-slate-800">
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100"><Target className="h-3.5 w-3.5" strokeWidth={2.2} /></span>
                الرسم والتحريك على الملعب
              </h3>
              <div className="flex flex-wrap items-center gap-1.5">
                {diagrams.map((d, i) => (
                  <button key={d.id} onClick={async () => { await flushPitchSave(); setActiveDiagram(d); setDiagramVersion((v) => v + 1); }}
                    className={`h-8 rounded-lg border px-2.5 text-[11px] font-bold transition-all active:scale-95 ${activeDiagram?.id === d.id ? 'border-teal-300 bg-teal-50 text-teal-700' : 'border-slate-200 bg-white text-slate-500 hover:border-teal-200'}`}
                    data-testid={`exercise-diagram-tab-${i}`}>
                    {d.title}
                  </button>
                ))}
                <button onClick={() => openDiagram({})} className="flex h-8 items-center gap-1 rounded-lg border border-dashed border-slate-300 px-2.5 text-[11px] font-bold text-slate-500 transition-all hover:border-teal-300 hover:text-teal-700 active:scale-95" data-testid="exercise-add-diagram-btn">
                  <Plus className="h-3 w-3" /> رسم جديد
                </button>
                {activeDiagram?.id && (
                  <button onClick={() => deleteDiagram(activeDiagram.id)} title="حذف هذا الرسم" className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-red-500 transition-colors hover:border-red-300" data-testid="exercise-delete-diagram-btn">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>
            <div className="p-3">
              {(activeDiagram !== null || diagrams.length === 0) ? (
                <PitchEditor
                  key={`${activeDiagram?.id || 'new'}-${diagramVersion}`}
                  ref={pitchEditorRef}
                  value={activeDiagram?.scene_json}
                  readOnly={!isOwner}
                  onChange={handleDiagramChange}
                  onSaved={() => {}}
                  rosterPlayers={form.category_id ? scope.players.filter((p) => p.category_id === form.category_id) : []}
                  minPlayers={form.min_players}
                  tasyarIconUrl={brand.iconUrl}
                  orgLogoUrl={selectedMembership?.club_logo_file_id ? `${API}/files/${selectedMembership.club_logo_file_id}` : null}
                />
              ) : (
                <div className="py-10 text-center text-xs text-slate-400">اختر رسماً من الأعلى أو أنشئ رسماً جديداً</div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div
        className="fixed inset-x-0 z-40 flex gap-2 border-t border-slate-200 bg-white/92 px-3 py-2.5 shadow-[0_-4px_18px_rgba(15,23,42,0.1)] backdrop-blur-md lg:hidden"
        style={{ bottom: 'calc(60px + env(safe-area-inset-bottom, 0px))' }}
        data-testid="exercise-mobile-save-bar"
      >
        <SaveActions saving={saving} saveIndicator={saveIndicator} onDraft={handleSaveDraft} onReady={handlePublishReady} />
      </div>

      {ReactDOM.createPortal(
        <div
          ref={printRef}
          className="print-area exercise-print-sheet"
          style={{ position: 'fixed', top: 0, left: '-9999px', width: '760px', background: '#ffffff', padding: '28px 28px 6px 28px', direction: 'rtl', fontFamily: "'Noto Sans Arabic', Arial, sans-serif" }}
          data-testid="exercise-print-sheet"
        >
          <div style={{ borderBottom: '3px solid #16233b', paddingBottom: 12, marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <h1 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', margin: 0 }}>{form.title || 'تمرين تدريبي'}</h1>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>تسيار — منصة إدارة الأندية والأكاديميات الرياضية</div>
            </div>
            {brand.iconUrl && <img src={brand.iconUrl} alt="" style={{ height: 40, width: 40, objectFit: 'contain' }} />}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 16, fontSize: 12 }}>
            <MetaBox label="النوع" value={exerciseTypeLabel} />
            <MetaBox label="المستوى" value={LEVEL_LABELS[form.level]} />
            {form.age_category && <MetaBox label="الفئة العمرية" value={form.age_category} />}
            {(form.min_players || form.max_players) && <MetaBox label="عدد اللاعبين" value={`${form.min_players || '—'} - ${form.max_players || '—'}`} />}
            {form.duration_minutes && <MetaBox label="المدة" value={`${form.duration_minutes} دقيقة`} />}
            {form.field_space && <MetaBox label="مساحة الملعب" value={form.field_space} />}
            {orgName && <MetaBox label="المنشأة" value={orgName} />}
            {teamName && <MetaBox label="الفريق" value={teamName} />}
            {categoryName && <MetaBox label="الفئة" value={categoryName} />}
          </div>

          {(form.short_description || form.technical_objective) && (
            <PrintSection title="الوصف والهدف">
              {form.short_description && <p style={{ margin: '0 0 6px' }}>{form.short_description}</p>}
              {form.technical_objective && <p style={{ margin: 0 }}><strong>الهدف الفني: </strong>{form.technical_objective}</p>}
            </PrintSection>
          )}

          {form.steps.length > 0 && (
            <PrintSection title="مراحل التنفيذ">
              <ol style={{ margin: 0, paddingRight: 18 }}>
                {form.steps.map((s, i) => <li key={i} style={{ marginBottom: 4 }}>{s.title}{s.description ? ` — ${s.description}` : ''}</li>)}
              </ol>
            </PrintSection>
          )}

          {(form.equipment.length > 0 || form.tags.length > 0) && (
            <PrintSection title="الأدوات والوسوم">
              {form.equipment.length > 0 && <p style={{ margin: '0 0 4px' }}><strong>الأدوات: </strong>{form.equipment.join('، ')}</p>}
              {form.tags.length > 0 && <p style={{ margin: 0 }}><strong>الوسوم: </strong>{form.tags.join('، ')}</p>}
            </PrintSection>
          )}

          {form.coaching_points.length > 0 && <PrintSection title="نقاط التدريب والتعليمات"><BulletList items={form.coaching_points} /></PrintSection>}
          {form.common_mistakes.length > 0 && <PrintSection title="الأخطاء الشائعة"><BulletList items={form.common_mistakes} /></PrintSection>}
          {form.success_indicators.length > 0 && <PrintSection title="مؤشرات النجاح"><BulletList items={form.success_indicators} /></PrintSection>}
          {form.safety_requirements.length > 0 && <PrintSection title="اشتراطات السلامة"><BulletList items={form.safety_requirements} /></PrintSection>}

          {pitchImages.length > 0 && (
            <PrintSection title={pitchImages.length > 1 ? 'الرسومات التكتيكية' : 'الرسم على الملعب'} style={{ marginBottom: 0 }}>
              <div>
                {pitchImages.map((p, i) => (
                  <div key={i} style={{ marginBottom: i < pitchImages.length - 1 ? 14 : 0, breakInside: 'avoid', pageBreakInside: 'avoid' }}>
                    {pitchImages.length > 1 && <p style={{ margin: '0 0 6px', fontWeight: 700, fontSize: 13 }}>{p.title}</p>}
                    <img src={p.img} alt={p.title} style={{ width: '100%', borderRadius: 8, border: '1px solid #e2e8f0' }} />
                  </div>
                ))}
              </div>
            </PrintSection>
          )}
        </div>,
        document.body
      )}

      {showAiModal && <AiSuggestModal onClose={() => setShowAiModal(false)} onApply={handleApplyAiSuggestion} />}

      <Modal open={!!pendingAiDiagram} title="يوجد رسم تكتيكي حالي على الملعب" onClose={() => { setPendingAiDiagram(null); toast.success('تم توليد نص التمرين، بقي الرسم الحالي بلا تغيير'); }}>
        <p className="mb-4 text-sm text-slate-600">اقترح الذكاء الاصطناعي رسماً تكتيكياً جديداً. هل تريد استبدال الرسم الحالي به أم إضافته إلى العناصر الموجودة؟</p>
        <div className="flex flex-col gap-2 sm:flex-row">
          <button
            data-testid="ai-diagram-replace-btn"
            onClick={async () => { const p = pendingAiDiagram; setPendingAiDiagram(null); await applyAiDiagram(p.rawDiagram, p.sid, p.targetDiagram, 'replace'); }}
            className="flex-1 rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-bold text-white hover:bg-slate-800"
          >
            استبدال الرسم الحالي
          </button>
          <button
            data-testid="ai-diagram-merge-btn"
            onClick={async () => { const p = pendingAiDiagram; setPendingAiDiagram(null); await applyAiDiagram(p.rawDiagram, p.sid, p.targetDiagram, 'merge'); }}
            className="flex-1 rounded-lg border border-teal-500 px-4 py-2.5 text-sm font-bold text-teal-600 hover:bg-teal-50"
          >
            إضافة إلى الرسم الحالي
          </button>
          <button
            data-testid="ai-diagram-cancel-btn"
            onClick={() => { setPendingAiDiagram(null); toast.success('تم توليد نص التمرين، بقي الرسم الحالي بلا تغيير'); }}
            className="flex-1 rounded-lg border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-500 hover:bg-slate-50"
          >
            إلغاء
          </button>
        </div>
      </Modal>
    </div>
  );
}
